// Stale-chunk detection + one-time reload guard. After a redeploy a cached
// page can reference a JS chunk hash that no longer exists; the browser throws
// a ChunkLoadError and Next.js renders a generic "client-side exception". A
// single hard reload fetches the fresh bundle and self-heals. The
// sessionStorage guard prevents an infinite loop.

export const CHUNK_RELOAD_KEY = "chunk-reload-attempted"

export function isChunkLoadError(
  error: { name?: string; message?: string } | null | undefined,
): boolean {
  if (!error) return false
  if (error.name === "ChunkLoadError") return true
  const msg = error.message ?? ""
  return (
    /ChunkLoadError/i.test(msg) ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /is not a valid JavaScript MIME type/i.test(msg)
  )
}

/**
 * Reload the page once to recover from a stale-chunk error. Returns `true`
 * if a reload was triggered (caller should stop rendering), `false` if the
 * error is not a chunk error or a reload was already attempted this session.
 */
export function attemptChunkReload(error: { name?: string; message?: string }): boolean {
  if (globalThis.window === undefined) return false
  if (!isChunkLoadError(error)) return false
  try {
    if (globalThis.sessionStorage.getItem(CHUNK_RELOAD_KEY) === "1") return false
    globalThis.sessionStorage.setItem(CHUNK_RELOAD_KEY, "1")
    globalThis.location.reload()
    return true
  } catch {
    return false
  }
}

/** Clear the one-time guard after a successful load so future redeploys self-heal. */
export function clearChunkReloadGuard(): void {
  if (globalThis.window === undefined) return
  try {
    globalThis.sessionStorage.removeItem(CHUNK_RELOAD_KEY)
  } catch {
    // sessionStorage unavailable (private mode / blocked) - nothing to clear.
  }
}
